import React, { useState } from "react";
import {
  LightModeOutlined,
  DarkModeOutlined,
  Menu as MenuIcon,
  SettingsOutlined,
  LogoutOutlined,
  PersonOutlineOutlined,
  VisibilityOutlined,
  CheckOutlined,
} from "@mui/icons-material";
import FlexBetween from "components/FlexBetween";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setMode, logout } from "state";
import profileImage from "assets/profile.jpeg";
import { usePageTitle } from "context/PageTitleContext";
import { useViewAs } from "context/ViewAsContext";
import { ROLES, ROLE_LABELS } from "config/roles";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";

const Navbar = ({ user, isSidebarOpen, setIsSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const { title } = usePageTitle();
  const { realRole, viewRole, effectiveRole, setViewRole } = useViewAs();
  const isRealAdmin = realRole === "admin";
  const previewing = isRealAdmin && !!viewRole;
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewAnchor, setViewAnchor] = useState(null);
  const isOpen = Boolean(anchorEl);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        backgroundImage: "none",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* LEFT — hamburger (mobile only) + current page title */}
        <FlexBetween gap="0.5rem" sx={{ minWidth: 0 }}>
          {!isNonMobile && (
            <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight={700} color="text.primary" noWrap>
            {title}
          </Typography>
        </FlexBetween>

        {/* RIGHT */}
        <FlexBetween gap="0.75rem">
          {/* View-as-role (admins only) — preview the app as a lower role */}
          {isRealAdmin && (
            <>
              <Tooltip title="Preview the app as another role">
                <Chip
                  icon={<VisibilityOutlined sx={{ fontSize: 18 }} />}
                  label={
                    isNonMobile
                      ? `View: ${ROLE_LABELS[effectiveRole] || effectiveRole}`
                      : ROLE_LABELS[effectiveRole] || effectiveRole
                  }
                  onClick={(e) => setViewAnchor(e.currentTarget)}
                  color={previewing ? "warning" : "default"}
                  variant={previewing ? "filled" : "outlined"}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
              <Menu
                anchorEl={viewAnchor}
                open={Boolean(viewAnchor)}
                onClose={() => setViewAnchor(null)}
                slotProps={{ paper: { sx: { minWidth: 180, mt: 1 } } }}
              >
                <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                  View the app as
                </Typography>
                {ROLES.map((r) => (
                  <MenuItem
                    key={r}
                    selected={effectiveRole === r}
                    onClick={() => {
                      setViewRole(r === "admin" ? null : r);
                      setViewAnchor(null);
                      navigate("/dashboard");
                    }}
                  >
                    <ListItemIcon>
                      {effectiveRole === r ? <CheckOutlined fontSize="small" /> : <Box sx={{ width: 20 }} />}
                    </ListItemIcon>
                    {ROLE_LABELS[r]}
                    {r === realRole ? " (you)" : ""}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          <Tooltip title={theme.palette.mode === "dark" ? "Light mode" : "Dark mode"}>
            <IconButton onClick={() => dispatch(setMode())}>
              {theme.palette.mode === "dark" ? (
                <LightModeOutlined />
              ) : (
                <DarkModeOutlined />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Settings">
            <IconButton onClick={() => navigate("/system-setup")}>
              <SettingsOutlined />
            </IconButton>
          </Tooltip>

          <Tooltip title="Account">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
              <Avatar src={profileImage} sx={{ width: 34, height: 34 }} />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={isOpen}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography fontWeight={700} fontSize={14} noWrap>
                {user?.name || "Account"}
              </Typography>
              <Typography color="text.secondary" fontSize={12} noWrap>
                {user?.email || user?.role || ""}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate("/system/bussiness-settings");
              }}
            >
              <ListItemIcon>
                <PersonOutlineOutlined fontSize="small" />
              </ListItemIcon>
              Profile & Business
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate("/system-setup");
              }}
            >
              <ListItemIcon>
                <SettingsOutlined fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutOutlined fontSize="small" />
              </ListItemIcon>
              Log out
            </MenuItem>
          </Menu>
        </FlexBetween>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
